/**
 * DealLibraryPage — Completed deals table with Duplicate / Run again / Edit / Archive.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, BookOpen, RefreshCw, Trophy, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';

const TYPE_LABEL = {
  footfall_filler: 'Footfall Filler', basket_builder: 'Basket Builder',
  community_event: 'Community Event', local_group: 'Local Group',
  waste_rescue: 'Waste Rescue', repeat_visit: 'Repeat Visit',
  weather_triggered: 'Weather Triggered', seasonal_promotion: 'Seasonal',
};

const fmt = (n) => (n == null || isNaN(n) ? '—' : `£${Number(n).toFixed(2)}`);

export default function DealLibraryPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await api.get('/deals/library');
      setDeals(res.data.deals || []);
    } catch {
      toast.error('Failed to load library');
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const runAgain = async (deal) => {
    setBusyId(deal.id);
    try {
      const res = await api.post(`/deals/library/${deal.id}/run-again`);
      toast.success('Duplicated as new draft');
      navigate(`/marketing/deals-calendar/${res.data.id}`);
    } catch {
      toast.error('Failed to duplicate');
    } finally { setBusyId(null); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4" data-testid="deal-library-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/marketing/deals-calendar')}>
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to Calendar
      </Button>
      <div>
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <BookOpen className="w-4 h-4" /> Marketing / Deal Library
        </div>
        <h1 className="text-3xl font-bold font-heading mt-1">Your Completed Deals</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Reuse what worked. Rerun a proven deal as a new draft.
        </p>
      </div>

      {loading ? (
        <div className="p-10 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>
      ) : deals.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-muted-foreground">
          <Trophy className="w-8 h-8 mx-auto mb-2" />
          <div className="font-medium mb-1">Your library is empty</div>
          <p className="text-sm">Completed deals with final reviews will show up here.</p>
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="library-table">
              <thead className="border-b bg-muted/50 text-left">
                <tr>
                  <th className="p-3">Name</th>
                  <th className="p-3">Type</th>
                  <th className="p-3">Period</th>
                  <th className="p-3 text-right">Incr. revenue</th>
                  <th className="p-3 text-right">Incr. GP</th>
                  <th className="p-3">Perf.</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {deals.map((d) => (
                  <tr key={d.id} className="border-b hover:bg-accent/40" data-testid={`library-row-${d.id}`}>
                    <td className="p-3 font-medium">{d.name}</td>
                    <td className="p-3"><Badge variant="outline">{TYPE_LABEL[d.deal_type] || d.deal_type}</Badge></td>
                    <td className="p-3 text-muted-foreground">
                      {d.start_date || '—'} → {d.end_date || '—'}
                    </td>
                    <td className="p-3 text-right">{fmt(d.final_incremental_revenue)}</td>
                    <td className="p-3 text-right">{fmt(d.final_incremental_gp)}</td>
                    <td className="p-3">
                      {d.performance_status ? (
                        <Badge variant="outline">{d.performance_status.replace(/_/g, ' ')}</Badge>
                      ) : '—'}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/marketing/deals-calendar/${d.id}`)}>
                          View
                        </Button>
                        <Button size="sm" onClick={() => runAgain(d)} disabled={busyId === d.id} data-testid={`run-again-${d.id}`}>
                          {busyId === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                          Run again
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
